import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { usePageSEO } from "../lib/seo";
import { ArrowLeft, Calendar, Tag, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";

const API = process.env.REACT_APP_BACKEND_URL;

export default function InsightsArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/insights/article/${slug}`);
        if (!res.ok) throw new Error("Not found");
        setArticle(await res.json());
      } catch (e) { console.error("Article load failed:", e); }
      finally { setLoading(false); }
    };
    load();
  }, [slug]);

  usePageSEO({
    title: article?.seo?.meta_title || article?.title || "Insights",
    description: article?.seo?.meta_description || article?.subtitle || "",
    path: `/insights/${slug}`,
    ogTitle: article?.seo?.meta_title || article?.title,
    ogDescription: article?.seo?.meta_description || article?.subtitle,
  });

  if (loading) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><div className="w-6 h-6 border-2 border-[#0033CC] border-t-transparent rounded-full animate-spin" /></div>;
  if (!article) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><p className="text-lg text-slate-400 font-['Outfit']">Article not found</p></div>;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="article-page">
      {/* Nav */}
      <header className="backdrop-blur-2xl bg-white/80 border-b border-slate-200/60 sticky top-0 z-50 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto flex items-center justify-between h-16">
          <Link to="/"><img src="/stansbury-logo.png" alt="Stansbury AI" className="h-10 sm:h-12" /></Link>
          <div className="flex items-center gap-6">
            <Link to="/insights" className="text-sm font-['Outfit'] text-slate-500 hover:text-[#0033CC] transition-colors">Insights</Link>
            <Link to="/blueprint" className="bg-[#0033CC] text-white px-5 py-2 text-sm font-['Outfit'] font-medium hover:bg-[#002299] transition-colors">Get Your Blueprint</Link>
          </div>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 md:px-12 py-12">
        {/* Back */}
        <Link to="/insights" className="inline-flex items-center gap-1.5 text-sm text-slate-400 font-['Outfit'] hover:text-[#0033CC] transition-colors mb-8" data-testid="back-to-insights">
          <ArrowLeft className="w-3.5 h-3.5" /> All Insights
        </Link>

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-4">
            {article.tags?.map(tag => (
              <span key={tag} className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-[#0033CC] bg-[#0033CC]/5 px-2 py-0.5">{tag}</span>
            ))}
          </div>
          <h1 className="text-3xl sm:text-4xl font-['Outfit'] font-medium text-[#0F172A] tracking-tight leading-[1.15] mb-4" data-testid="article-title">{article.title}</h1>
          {article.subtitle && <p className="text-lg text-slate-500 font-['IBM_Plex_Sans'] leading-relaxed mb-6">{article.subtitle}</p>}
          <div className="flex items-center gap-4 text-xs text-slate-400 font-['IBM_Plex_Sans'] mb-8 pb-8 border-b border-slate-200">
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{article.published_at ? new Date(article.published_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : ""}</span>
            <span>Stansbury AI</span>
          </div>
        </motion.div>

        {/* Inline CTA - Top */}
        <div className="bg-[#0033CC]/5 border border-[#0033CC]/10 p-5 mb-8">
          <p className="text-sm font-['IBM_Plex_Sans'] text-[#0F172A]">
            <strong className="font-['Outfit']">Want to know your AI readiness score?</strong> Get a free diagnostic across 6 growth dimensions.
            <Link to="/blueprint" className="text-[#0033CC] font-medium ml-1 hover:underline">Start your Blueprint &rarr;</Link>
          </p>
        </div>

        {/* Body */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="prose-stansbury" data-testid="article-body">
          <ReactMarkdown
            components={{
              h2: ({ children }) => <h2 className="text-xl font-['Outfit'] font-medium text-[#0F172A] mt-10 mb-4 tracking-tight">{children}</h2>,
              h3: ({ children }) => <h3 className="text-lg font-['Outfit'] font-medium text-[#0F172A] mt-8 mb-3">{children}</h3>,
              p: ({ children }) => <p className="text-[15px] text-slate-600 font-['IBM_Plex_Sans'] leading-[1.8] mb-4">{children}</p>,
              ul: ({ children }) => <ul className="space-y-2 mb-6 ml-4">{children}</ul>,
              ol: ({ children }) => <ol className="space-y-2 mb-6 ml-4 list-decimal">{children}</ol>,
              li: ({ children }) => <li className="text-[15px] text-slate-600 font-['IBM_Plex_Sans'] leading-[1.7] pl-1">{children}</li>,
              strong: ({ children }) => <strong className="font-semibold text-[#0F172A]">{children}</strong>,
              blockquote: ({ children }) => <blockquote className="border-l-3 border-[#0033CC] pl-4 py-2 my-6 bg-slate-50">{children}</blockquote>,
              a: ({ href, children }) => <a href={href} className="text-[#0033CC] font-medium hover:underline" target="_blank" rel="noopener noreferrer">{children}</a>,
            }}
          >
            {article.body}
          </ReactMarkdown>
        </motion.div>

        {/* End CTA Block */}
        <div className="mt-12 bg-[#0F172A] p-8 md:p-10" data-testid="article-end-cta">
          <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#818CF8] mb-3">Your Next Move</p>
          <h2 className="text-2xl font-['Outfit'] font-medium text-white mb-3">Get Your AI Growth Blueprint</h2>
          <p className="text-sm text-slate-400 font-['IBM_Plex_Sans'] leading-relaxed mb-6 max-w-lg">
            Discover exactly where AI can drive revenue, cut costs, and build competitive advantage for your business. Takes 10 minutes. Delivers a 13-page executive report.
          </p>
          <Link to="/blueprint" className="inline-flex items-center gap-2 bg-[#0033CC] text-white px-8 py-3 font-['Outfit'] font-semibold text-sm hover:bg-[#002299] transition-colors" data-testid="article-cta-btn">
            Begin Your Blueprint <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Back to Insights */}
        <div className="mt-8 text-center">
          <Link to="/insights" className="text-sm text-slate-400 font-['Outfit'] hover:text-[#0033CC] transition-colors">
            <ArrowLeft className="w-3.5 h-3.5 inline mr-1" /> Back to All Insights
          </Link>
        </div>
      </article>
    </div>
  );
}
